import React from "react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { nullable } from "diagnostics-lib/io";
import { buildOptions, FilterWrapper, NONE_LABEL, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import Switch from "@ui/Switch";
import t from "../../../../i18n";
import Reset from "@ui/Filter/Reset";
import { useProjects } from "../../../../api/useProjects";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { useCategories } from "../../../../api/useCategories";
import { useTranslations } from "../../../../api/useTranslations";

const FilterValues = io.strict({
  project: io.array(io.string),
  projectType: io.array(nullable(io.string)),
  projectSite: io.array(nullable(io.string)),
  system: io.array(nullable(io.string)),
});

type FilterValues = io.TypeOf<typeof FilterValues>;

const DEFAULT_FILTER_VALUES: FilterValues = {
  project: [],
  projectType: [],
  projectSite: [],
  system: [],
};
export const useFilterValues = createGlobalState({
  storage: "session",
  key: "adminQuantitiesTableFilters",
  type: FilterValues,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: false,
});

interface P {
  onDisplayDraftChange: () => void;
  displayDraft: boolean;
  searchState: readonly [string, (n: string) => void];
  resetPagination: () => void;
  filterValues: FilterValues;
  setFilterValues: (v: React.SetStateAction<FilterValues>) => void;
}

const QuantityFilters = ({
  onDisplayDraftChange,
  displayDraft,
  searchState,
  resetPagination,
  filterValues,
  setFilterValues,
}: P) => {
  const projects = useProjects();
  const translations = useTranslations();
  const systems = useCategories("SYSTEM");
  const [search, setSearch] = searchState;

  const includedProjects = React.useMemo(
    () => projects.filter(({ draft }) => !draft || (displayDraft && draft)),
    [displayDraft, projects],
  );

  const projectOptions = React.useMemo(
    () =>
      buildOptions(
        includedProjects.map(p => ({
          label: p.name,
          value: p.id.toString(),
        })),
      ),
    [includedProjects],
  );

  const projectTypeOptions = React.useMemo(() => {
    const allOptions = includedProjects.map(p => ({
      label: p.typeId ?? NONE_LABEL,
      value: p.typeId ?? null,
    }));
    return buildOptions(allOptions);
  }, [includedProjects]);

  const projectSiteOptions = React.useMemo(() => {
    const allOptions = includedProjects.map(p => ({
      label: p.siteId ?? NONE_LABEL,
      value: p.siteId ?? null,
    }));
    return buildOptions(allOptions);
  }, [includedProjects]);

  const systemOptions = React.useMemo(
    () =>
      buildOptions(
        [{ label: NONE_LABEL, value: null as string | null }].concat(
          systems.map(s => ({ label: s, value: s })),
        ),
      ),
    [systems],
  );

  const handleSearchChange = React.useCallback(
    (search: string) => {
      resetPagination();
      setSearch(search);
    },
    [resetPagination, setSearch],
  );

  return (
    <>
      <SearchInputV2
        data-cy="search-quantities"
        placeholder={t("commons.placeholder.search")}
        value={search}
        onChange={e => handleSearchChange(e.currentTarget.value)}
      />
      <FilterWrapper>
        <FilterSelect<string>
          title={t("administration.quantities.table.header.project_name")}
          options={projectOptions}
          filtered
          values={filterValues["project"]}
          setValues={useSubStateSetter(setFilterValues, "project", resetPagination)}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.project_type", translations)}
          options={projectTypeOptions}
          filtered
          values={filterValues["projectType"]}
          setValues={useSubStateSetter(setFilterValues, "projectType", resetPagination)}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.project_site")}
          options={projectSiteOptions}
          filtered
          values={filterValues["projectSite"]}
          setValues={useSubStateSetter(setFilterValues, "projectSite", resetPagination)}
        />
        <FilterSelect<string | null>
          title={t("administration.quantities.table.header.system")}
          options={systemOptions}
          filtered
          values={filterValues["system"]}
          setValues={useSubStateSetter(setFilterValues, "system", resetPagination)}
        />
        <Reset
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </FilterWrapper>
      <Switch checked={displayDraft} onChange={onDisplayDraftChange}>
        {t("projects.header.display_draft")}
      </Switch>
    </>
  );
};

export default QuantityFilters;
